import React, { useState, useRef, useEffect } from 'react';
import { Microphone, Stop, Trash, PaperPlaneRight } from '@phosphor-icons/react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const VoiceRecorder = ({ onVoiceSent, onClose }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('❌ Your browser does not support voice recording. Please use Chrome, Safari, or Firefox.');
        return;
      }

      console.log('🎤 Requesting microphone permission...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('✅ Microphone permission granted');
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioURL(url);
        setAudioBlob(blob);
        
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('❌ Failed to start recording:', error);
      
      let errorMessage = '❌ Microphone access denied.\n\n';
      
      if (error.name === 'NotAllowedError') {
        errorMessage += '📱 Please allow microphone permission in your browser settings.\n\n';
        errorMessage += 'Settings → Site Permissions → Microphone → Allow';
      } else if (error.name === 'NotFoundError') {
        errorMessage += '🎤 No microphone found. Please check if your microphone is connected.';
      } else if (error.name === 'NotReadableError') {
        errorMessage += '🎤 Microphone is being used by another app. Please close other apps and try again.';
      } else {
        errorMessage += `Error: ${error.message}`;
      }
      
      alert(errorMessage);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const deleteRecording = () => {
    setAudioURL(null);
    setAudioBlob(null);
    setRecordingTime(0);
  };

  const sendVoice = async () => {
    if (!audioBlob) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'voice.webm');
      formData.append('media_type', 'voice');

      const response = await axios.post(`${API}/upload/media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const uploadedFile = response.data?.file || response.data;
      if (!uploadedFile?.url) {
        throw new Error('Upload completed but no media URL was returned');
      }

      onVoiceSent({
        type: 'voice',
        url: uploadedFile?.url,
        filename: uploadedFile?.filename,
        duration: recordingTime
      });

      onClose();
    } catch (error) {
      console.error('Failed to upload voice:', error);
      alert('Failed to send voice message');
    } finally {
      setIsUploading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">🎤 Voice Message</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        <div className="flex flex-col items-center gap-4">
          {/* Recording Time */}
          <div className="text-3xl font-mono text-green-600">
            {formatTime(recordingTime)}
          </div>

          {/* Waveform Animation */}
          {isRecording && (
            <div className="flex items-center gap-1 h-16">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="w-1 bg-green-500 rounded-full animate-pulse"
                  style={{
                    height: `${Math.random() * 100}%`,
                    animationDelay: `${i * 0.1}s`
                  }}
                />
              ))}
            </div>
          )}

          {/* Audio Player */}
          {audioURL && (
            <audio src={audioURL} controls className="w-full" />
          )}

          {/* Controls */}
          <div className="flex gap-3 mt-4">
            {!isRecording && !audioURL && (
              <button
                onClick={startRecording}
                className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600"
              >
                <Microphone size={24} weight="fill" />
                Start Recording
              </button>
            )}

            {isRecording && (
              <button
                onClick={stopRecording}
                className="flex items-center gap-2 px-6 py-3 bg-red-500 text-white rounded-full hover:bg-red-600"
              >
                <Stop size={24} weight="fill" />
                Stop
              </button>
            )}

            {audioURL && (
              <>
                <button
                  onClick={deleteRecording}
                  className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  <Trash size={20} />
                  Delete
                </button>
                <button
                  onClick={sendVoice}
                  disabled={isUploading}
                  className="flex items-center gap-2 px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
                >
                  <PaperPlaneRight size={20} weight="fill" />
                  {isUploading ? 'Sending...' : 'Send'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceRecorder;
